import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { MARKETPLACE_CATEGORY_LABELS } from "@/lib/causes";
import ImageUploader from "@/components/ImageUploader";

export default function NewListing() {
  const nav = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "tutoring",
    kind: "service",
    price_hours: 2,
    image_url: "",
    active: true,
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [notAllowed, setNotAllowed] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/marketplace/listings/${id}`)
      .then(({ data }) => {
        if (user && data.seller_id !== user.id && user.role !== "admin") {
          setNotAllowed(true);
          return;
        }
        setForm({
          title: data.title || "",
          description: data.description || "",
          category: data.category || "tutoring",
          kind: data.kind || "service",
          price_hours: data.price_hours ?? 2,
          image_url: data.image_url || "",
          active: data.active !== false,
        });
      })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [id, isEdit, user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        kind: form.kind,
        price_hours: Number(form.price_hours),
        image_url: form.image_url,
      };
      if (isEdit) {
        payload.active = form.active;
        const { data } = await api.patch(`/marketplace/listings/${id}`, payload);
        nav(`/marketplace/${data.id}`);
      } else {
        const { data } = await api.post("/marketplace/listings", payload);
        nav(`/marketplace/${data.id}`);
      }
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-10 text-ink-soft" data-testid="listing-edit-loading">Loading…</div>
      </Layout>
    );
  }
  if (notAllowed) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-10" data-testid="listing-edit-forbidden">
          <h1 className="font-display font-bold text-2xl">Not your listing</h1>
          <p className="text-ink-soft mt-2">Only the seller can edit this one.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="new-listing-page" className="max-w-2xl mx-auto px-5 md:px-8 py-12">
        <span className="overline text-[var(--cm-primary)]">{isEdit ? "Edit" : "Marketplace"}</span>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight">
          {isEdit ? "Edit listing" : "Offer a service or good"}
        </h1>
        <p className="text-ink-soft mt-2">
          {isEdit
            ? "Existing orders keep their original terms — new redemptions use the updated details."
            : "Other members can redeem with their earned Time Hours."}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <Field label="Title" testid="new-listing-title-field">
            <input
              data-testid="new-listing-title-input"
              required
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
              placeholder="1-hour Spanish conversation practice"
            />
          </Field>
          <Field label="Description" testid="new-listing-desc-field">
            <textarea
              data-testid="new-listing-description-input"
              required
              rows={5}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
              placeholder="What's included? Any prep needed?"
            />
          </Field>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Category" testid="new-listing-category-field">
              <select
                data-testid="new-listing-category-input"
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
              >
                {Object.entries(MARKETPLACE_CATEGORY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </Field>
            <Field label="Type" testid="new-listing-kind-field">
              <select
                data-testid="new-listing-kind-input"
                value={form.kind}
                onChange={(e) => update("kind", e.target.value)}
                className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
              >
                <option value="service">Service</option>
                <option value="good">Good / physical item</option>
              </select>
            </Field>
            <Field label="Price (hours)" testid="new-listing-price-field">
              <input
                data-testid="new-listing-price-input"
                type="number"
                min={0.1}
                step="any"
                max={500}
                required
                inputMode="decimal"
                value={form.price_hours}
                onChange={(e) => update("price_hours", e.target.value)}
                className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
              />
            </Field>
          </div>
          <Field label="Photo (optional)" testid="new-listing-image-field">
            <ImageUploader
              value={form.image_url}
              onChange={(v) => update("image_url", v)}
              testidPrefix="listing"
            />
          </Field>
          {isEdit && (
            <label
              data-testid="listing-active-toggle"
              className="flex items-center gap-3 bg-app rounded-xl px-4 py-3 border border-ink-soft cursor-pointer"
            >
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => update("active", e.target.checked)}
                className="h-4 w-4 rounded border-ink-soft text-[var(--cm-primary)] focus:ring-[var(--cm-primary)]/40"
              />
              <div>
                <div className="font-semibold text-sm">Listing is active</div>
                <div className="text-xs text-ink-soft">
                  {form.active ? "Visible in the marketplace and open to redemption." : "Hidden from browsers — existing orders unaffected."}
                </div>
              </div>
            </label>
          )}
          {err && (
            <div data-testid="new-listing-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">
              {err}
            </div>
          )}
          <div className="flex items-center gap-3">
            <button data-testid="new-listing-submit-btn" type="submit" disabled={submitting} className="btn-primary disabled:opacity-60">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Post listing"}
            </button>
            {isEdit && (
              <button
                data-testid="new-listing-cancel-btn"
                type="button"
                onClick={() => nav(`/marketplace/${id}`)}
                className="btn-outline text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </Layout>
  );
}

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="block">
      <span className="text-xs uppercase tracking-widest font-semibold text-ink-soft">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
